import React, { useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Alert, Button, Input } from '@rallyshift/design-system'
import { useAuth } from '../contexts/AuthContext'
import { useLocale } from '../contexts/LocaleContext'
import { useOrganization } from '../contexts/OrganizationContext'
import AuthShell from '../components/AuthShell'
import { isPlatformHost } from '../api/tenant'

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const { signIn } = useAuth()
  const { t } = useLocale()
  const { organization } = useOrganization()
  const navigate = useNavigate()
  const location = useLocation()

  const staffHost = isPlatformHost()
  const returnTo =
    new URLSearchParams(location.search).get('returnTo') ||
    (staffHost ? '/platform' : '/dashboard')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')
    try {
      await signIn(email, password)
      navigate(returnTo)
    } catch (err: any) {
      setError(err.message || t('errors.loginError'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <AuthShell
      title="Welkom terug"
      subtitle={
        staffHost
          ? 'Log in met je RallyShift-account.'
          : organization
            ? `Log in om je diensten bij ${organization.name} te bekijken.`
            : 'Log in om je diensten te bekijken.'
      }
      footer={
        staffHost ? undefined : organization?.allow_public_signup ? (
          <>
            <span className="text-text-secondary">Nog geen account? </span>
            <button
              type="button"
              onClick={() => navigate('/signup')}
              className="font-semibold text-action-primary underline-offset-2 hover:underline"
            >
              Meld je aan als vrijwilliger
            </button>
          </>
        ) : (
          <span className="text-text-muted">
            Deze organisatie werkt op uitnodiging. Vraag de organisator om een uitnodiging.
          </span>
        )
      }
    >
      {error && (
        <div className="mb-4">
          <Alert variant="danger" onDismiss={() => setError('')}>
            {error}
          </Alert>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-5">
        <Input
          label={t('auth.email')}
          type="email"
          size="lg"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <Input
          label={t('auth.password')}
          type="password"
          size="lg"
          autoComplete="current-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />

        <Button type="submit" variant="tenant" size="lg" fullWidth loading={loading}>
          {t('auth.signIn')}
        </Button>

        {!staffHost && (
          <div className="text-center">
            <button
              type="button"
              onClick={() => navigate('/forgot-password')}
              className="text-sm text-text-secondary underline-offset-2 hover:underline"
            >
              {t('auth.forgotPassword')}
            </button>
          </div>
        )}
      </form>
    </AuthShell>
  )
}

export default LoginPage
